import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import {z} from 'zod';
import {NodeType} from 'common/src/modules/nodes/nodes.types';
import {V2CardAgentToolName} from '../../promptToCardAgentV2.enums';
import {ProductCardScratchpadState} from '../../scratchpad/productCardScratchpad.types';
import validateAgentScratchpad from '../../utils/general/validateAgentScratchpad';
import {ProductSelectionStrategyType} from 'common/src/modules/productSelectionStrategies/productSelectionStrategies.types';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {MacroItemTypeValue} from 'common/src/modules/macros/macros.types';
import {CLStore} from 'common/src/redux/store';
import {ProductMatchingOperator} from 'common/src/modules/decisionTree/decisionTree.types';
import isVideoCampaign from 'common/src/modules/campaigns/utils/isVideoCampaign';

const setMatchingConditionsToolSchema = z.object({
  conditions: z
    .array(
      z.object({
        feedColumn: z.string().describe('The field to match on'),
        productMatchingOperator: z
          .nativeEnum(ProductMatchingOperator)
          .describe(
            'The operator to use for matching (1 - Equals, 2 - Includes, 3 - Included in, 4 - Less than or equal, 5 - Greater than or equal)',
          ),
        signalKey: z.string().describe('The signal to match against'),
      }),
    )
    .describe('Array of matching conditions'),
});

export default function setMatchingConditionsTool(store: CLStore) {
  async function execute({
    input: {conditions},
    toolUtils: {agentScratchpad},
  }: ToolExecuteParams<
    typeof setMatchingConditionsToolSchema,
    ProductCardScratchpadState
  >): Promise<ToolResult> {
    const validatedScratchpad = validateAgentScratchpad<ProductCardScratchpadState>(
      agentScratchpad,
      NodeType.PRODUCTS,
    );

    const {campaign, versionId} = store.getState().campaignPage.campaignTree;

    if (!campaign) {
      return toolError('Campaign is required to set matching conditions');
    }

    if (!versionId) {
      return toolError('Version ID is required to set matching conditions');
    }

    if (!validatedScratchpad.card.feedId) {
      return toolError('No feed ID found, you need to set a feed first');
    }

    if (!validatedScratchpad.card.feed) {
      return toolError('Feed is required to set matching conditions');
    }

    const matchingStrategy = isVideoCampaign(campaign.type)
      ? ProductSelectionStrategyType.ProductMatchingVideo
      : ProductSelectionStrategyType.ProductMatching;

    if (validatedScratchpad.card.productsSelectionStrategy !== matchingStrategy) {
      return toolError('Product selection strategy is not product matching');
    }

    const conditionSignals = validatedScratchpad.card.availableSignals;

    if (!conditionSignals || conditionSignals.length === 0) {
      return toolError('No signals found, you need to search for signals first');
    }

    const columns = validatedScratchpad.card.availableFeedColumns;

    if (!columns || columns.length === 0) {
      return toolError('No feed columns found, you need to search for feed columns first');
    }

    const validatedConditions = conditions.map((condition, index) => {
      const feedColumn = columns.find((column) => column.value === condition.feedColumn);

      const signal = conditionSignals?.find((signal) => signal.signalKey === condition.signalKey);
      const conditionErrors: string[] = [];

      if (!feedColumn) {
        conditionErrors.push(
          `Feed column ${condition.feedColumn} is not available at index ${index}`,
        );
      }

      if (!signal) {
        conditionErrors.push(`Signal ${condition.signalKey} is not available at index ${index}`);
      }

      if (
        feedColumn?.type === MacroItemTypeValue.Numeric ||
        feedColumn?.type === MacroItemTypeValue.Date
      ) {
        if (
          ![
            ProductMatchingOperator.GreaterThanOrEqual,
            ProductMatchingOperator.LessThanOrEqual,
          ].includes(condition.productMatchingOperator)
        ) {
          conditionErrors.push(
            `Operator ${condition.productMatchingOperator} is not available for feed column ${condition.feedColumn} at index ${index}`,
          );
        }
      }

      if (
        feedColumn?.type !== MacroItemTypeValue.Numeric &&
        feedColumn?.type !== MacroItemTypeValue.Date
      ) {
        if (
          ![
            ProductMatchingOperator.Equals,
            ProductMatchingOperator.Contains,
            ProductMatchingOperator.ContainedIn,
          ].includes(condition.productMatchingOperator)
        ) {
          conditionErrors.push(
            `Operator ${condition.productMatchingOperator} is not available for feed column ${condition.feedColumn} at index ${index}`,
          );
        }
      }

      return {
        ...condition,
        errors: conditionErrors,
      };
    });

    const invalidConditions = validatedConditions.filter(
      (condition) => condition.errors.length > 0,
    );
    const validConditions = validatedConditions.filter(
      (condition) => condition.errors.length === 0,
    );

    validatedScratchpad.card.configuration = {
      conditions: validConditions.map((condition) => ({
        feedColumnSignalKey: condition.feedColumn,
        operator: condition.productMatchingOperator as ProductMatchingOperator,
        signalKey: condition.signalKey,
        feedColumnUIName: '',
        signalMainCategory: undefined,
        signalUIName: '',
      })),
      orderBy: 0,
      contentType: 0,
    };

    if (invalidConditions.length > 0) {
      return toolResult(
        JSON.stringify({
          status: 'Partial success',
          message: `Only some of the conditions were set because of the following errors: ${invalidConditions
            .map((condition) => condition.errors.join('\n'))
            .join('\n')}`,
        }),
      );
    }

    return toolResult(
      JSON.stringify({
        status: 'success',
        message: `Matching conditions set: ${conditions.length} conditions configured`,
      }),
    );
  }

  return tool({
    name: V2CardAgentToolName.SetMatchingConditions,
    displayName: 'Set matching conditions',
    description: 'Set conditions for matching products. Only for Product Matching strategies.',
    execute,
    inputSchema: setMatchingConditionsToolSchema,
  });
}
